import "./StandingGroupTable.css";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import {
  ISoccerStandingsRank,
  ISoccerStandingsTeamRank,
  IStandingGroupTableColumn,
} from "../../../../types";
import { appTournamentConfig, TOURNAMENT_ID } from "../../config/tournament";
import { useMediaQuery } from "../../hooks";

export const StandingGroupTable = ({
  data,
  columnsList,
  isGroupStageEnded,
  qualifiedThirdPlaceIds,
  liveTeamIds,
}: {
  data: ISoccerStandingsRank;
  columnsList: IStandingGroupTableColumn[];
  isGroupStageEnded: boolean;
  /** Set of contestantIds from the "3rd Place Ranking" pseudo-group's top 8.
   *  Their group-stage rank is 3, but they advance via the cross-group
   *  ranking, so they should display the qualified marker. */
  qualifiedThirdPlaceIds?: Set<string>;
  /** Contestants whose match is currently in play - get a LIVE marker. */
  liveTeamIds?: Set<string>;
}) => {
  const { t } = useTranslation();
  const isGroupOnlyLeague = appTournamentConfig[TOURNAMENT_ID]?.isGroupOnlyLeague;
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  // Standalone /standings route renders every group expanded by default.
  const isStandalone = useLocation().pathname === "/standings";

  const [isOpen, setIsOpen] = useState(
    !!isGroupOnlyLeague || isDesktop || isStandalone,
  );
  const teamList = data.teamRanks ?? [];

  // When any team in this group is in play, every row reserves the LIVE badge's
  // width (hidden on non-live rows) so the stat columns stay aligned. With no
  // live match the badge isn't rendered at all and the layout is unchanged.
  const groupHasLive =
    !!liveTeamIds && teamList.some((t) => liveTeamIds.has(t.contestantId));

  const isTeamQualified = (team: ISoccerStandingsTeamRank) => {
    if (isGroupOnlyLeague) return false;
    if (team.rankStatus && team.rank <= 2) return true;
    // WC 2026: rank-3 finisher qualifies via the cross-group "3rd Place
    // Ranking" if they are among its top 8 by rank.
    if (
      team.rank === 3 &&
      qualifiedThirdPlaceIds &&
      qualifiedThirdPlaceIds.has(team.contestantId)
    ) {
      return true;
    }
    return false;
  };

  const groupNameIdentifier = data.groupName?.split(" ")?.[1];
  const groupName = isGroupOnlyLeague
    ? t("standingsTab.fallBackGroupName")
    : t("standingsTab.groupName", {
        groupNameIdentifier,
      });

  // const handleLogoLoadError = (
  //   event: SyntheticEvent<HTMLImageElement, Event>
  // ) => {
  //   event.currentTarget.src = "/images/teamLogos/flag-fallback.png";
  // };

  return (
    <div className="group-table-container">
      <button
        className={`group-table-header ${
          isOpen ? "group-table-header-open" : ""
        }`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="group-name">{groupName}</span>
        <div className="group-info">
          {/* <div className="team-flags-list">
            {teamLogos.map((team) => (
              <img
                key={team.contestantId}
                className={`team-flag ${
                  isClubTournament ? "club-team-flag" : ""
                } ${isTeamQualified(team) ? "qualified" : ""}`}
                src={team.logoImgSrc}
                width={32}
                height={24}
                onError={handleLogoLoadError}
                alt={team.contestantName}
              />
            ))}
          </div> */}
          <img
            className={`group-info-arrow ${
              isOpen ? "group-info-up-arrow" : ""
            }`}
            src="/images/down-arrow.svg"
            height={24}
            width={24}
            alt={isOpen ? "up-arrow" : "down-arrow"}
          />
        </div>
      </button>
      {isOpen ? (
        <div className="group-table-details">
          <div className="group-table-row header-row">
            {columnsList.map((column) => (
              <span key={column.propKey} className={column.headerClassName}>
                {column.headerLabel}
              </span>
            ))}
          </div>
          <div>
            {teamList.map((team) => (
              <div
                key={team.contestantId}
                className={`group-table-row values-row ${
                  isTeamQualified(team) ? "qualified" : ""
                }`}
              >
                {columnsList.map((column) => (
                  <span key={column.propKey} className={column.valueClassName}>
                    {column.propKey === "contestantName" ? (
                      <>
                        {team.contestantCode ||
                          team.contestantShortName ||
                          team.contestantName ||
                          team.contestantClubName}
                        {groupHasLive && (
                          <span
                            className={`standing-live-badge ${
                              liveTeamIds?.has(team.contestantId)
                                ? ""
                                : "standing-live-badge--hidden"
                            }`}
                          >
                            <span className="standing-live-dot" />
                            {t("matchStatus.live")}
                          </span>
                        )}
                      </>
                    ) : (
                      team[column.propKey]
                    )}
                  </span>
                ))}
              </div>
            ))}
            {isGroupStageEnded && !isGroupOnlyLeague && (
              <div className="qualified-info">
                <img
                  src="/images/green-dot.svg"
                  height={6}
                  width={6}
                  alt="green-dot"
                />
                {t("standingsTab.qualified")}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};
